import * as Sentry from '@sentry/react';
import { tr } from './i18n/i18n';
import { createRoot } from 'react-dom/client';

/**
 * CRITICAL CSS IMPORT ORDER FOR VITE + PRIMEREACT:
 *
 * 1. theme-variables.css - MUST load FIRST to define CSS variables
 * 2. primeicons.css - Icon fonts
 * 3. primereact.min.css - PrimeReact base styles
 * 4. lara-light-cyan/theme.css - PrimeReact theme (will use our CSS variables)
 * 5. Third-party CSS (react-phone-input-2)
 * 6. Tailwind CSS (main.css)
 * 7. Component-specific CSS (index.css)
 * 8. PrimeReact overrides (prime-react-override.css) - MUST load LAST
 */

// STEP 2-4: PrimeReact core styles
import 'primeicons/primeicons.css';
import { PrimeReactProvider } from 'primereact/api';
import { Image } from 'primereact/image';
import 'primereact/resources/primereact.min.css';
import 'primereact/resources/themes/lara-light-cyan/theme.css';

// STEP 1b: Load our theme variable overrides AFTER the PrimeReact theme
// This allows us to override theme CSS variables defined by the theme
import './styles/theme-variables.css';

// STEP 5: Third-party libraries
import 'react-phone-input-2/lib/style.css';

// STEP 6-8: Application styles
import './styles/main.css';
import './styles/index.css';
import './styles/prime-react-override.css';

// i18n MUST be imported before App so translations are ready before first render
import './i18n/i18n';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import 'reflect-metadata';
import App from './app/App';
import { somethingWentWrongImage } from './assets/images';
import { AppButton, ErrorPageLayout } from './components';
import { GlobalNotifier } from './components/notify/GlobalNotifier';
import { APP_ROUTES } from './constants';
import { persistor, store } from './redux/store';
import { primeReactConfig } from './config/primereact.config';
import { SubscriptionProvider } from './hooks/useFeature';
import type { VitePreloadErrorEvent } from './interface/vite.interface';
import { useTranslation } from 'react-i18next';

const container = document.getElementById('root')!;
const root = createRoot(container);
const queryClient = new QueryClient();

const DSN_URL = import.meta.env.VITE_SENTRY_DSN;

if (!DSN_URL) {
  console.error('❌ Error: REACT_APP_SENTRY_DSN environment variable is not set');
} else {
  Sentry.init({
    dsn: DSN_URL,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const handleNavigate = () => {
  window.location.replace(APP_ROUTES.DASHBOARD);
};
const ErrorFallback = () => {
  const { t } = useTranslation();
  return (
    <ErrorPageLayout
      title={t('dialog.something_went_wrong', 'Something Went Wrong')}
      code="500"
      message="We're sorry, but an unexpected internal error occurred. Our team has been notified. Please try refreshing the page or return to the dashboard."
      image={<Image src={somethingWentWrongImage} alt="image" width="100%" />}
    >
      <AppButton label={t('common.go_to_dashboard', 'Go to dashboard')} onClick={handleNavigate} size="small" />
    </ErrorPageLayout>
  );
};

// Add after all imports, before creating root
window.addEventListener('vite:preloadError', (event: Event) => {
  const viteEvent = event as VitePreloadErrorEvent;
  const hasReloaded = sessionStorage.getItem('vite_reload_attempt');
  const errorDetail = viteEvent.payload;

  // Log error details for debugging
  console.error('💥 Vite chunk loading failed:', {
    error: errorDetail,
    hasReloaded: !!hasReloaded,
    timestamp: new Date().toISOString(),
  });

  // Report to Sentry for monitoring (if initialized)
  if (DSN_URL && Sentry.isInitialized()) {
    Sentry.captureException(new Error('Vite chunk loading failed'), {
      tags: {
        errorType: 'chunk_load_failure',
        reloadAttempt: hasReloaded ? 'second' : 'first',
      },
      extra: {
        chunkError: errorDetail,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      },
    });
  }

  if (!hasReloaded) {
    sessionStorage.setItem('vite_reload_attempt', '1');
    console.warn('🔄 First chunk load failed. User can manually reload if needed.');
    // window.location.reload(); // ✅ DISABLED to prevent reload loop
  } else {
    // ✅ FIX: Handle second consecutive failure
    sessionStorage.removeItem('vite_reload_attempt');
    console.warn('🔄 Second chunk load failed. Chunk loading error detected.');
    // window.location.reload(); // ✅ DISABLED to prevent reload loop
  }
});

window.addEventListener('load', () => {
  sessionStorage.removeItem('vite_reload_attempt');
});

root.render(
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <PrimeReactProvider value={primeReactConfig}>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <PersistGate loading={null} persistor={persistor}>
            <SubscriptionProvider>
              <GlobalNotifier />
              <App />
            </SubscriptionProvider>
          </PersistGate>
        </QueryClientProvider>
      </Provider>
    </PrimeReactProvider>
  </Sentry.ErrorBoundary>,
);
